import React, { useState } from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { productApi, ProductModels } from '@getgreenline/products'
import { PermissionNames } from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { Alert, message, Progress, Spin } from 'antd-v4'
import { Icon, Upload } from 'antd'
import { UploadFile } from 'antd/lib/upload/interface'
import { UploadChangeParam } from 'antd-v4/lib/upload'
import {
  GrRegex,
  parseErrorMsg,
  parseProductBulkUpdateErrorMsg,
  parseProductBulkUpdateErrorTitle,
  sleep,
  uploadFileToS3Bucket,
  uploadImagesAndGetCloudinaryUrlsSimultaneously,
} from '../../../../utilities/helpers'
import { isArray } from 'lodash'
import { CloseCircleFilled } from '@ant-design/icons'
import { CLOUDINARY } from '../../../../constants/general'
import * as Papa from 'papaparse'
import { CSVProductHeaders } from '../../../../constants/CSVHeaders'
import { MAX_BULK_UPDATE_SIZE } from '../../../../constants/CSVSizeLimits'

interface IFileObject {
  fileObj: File | Blob | undefined
  fullFileName: string
  fileExtension: string
  contentType: string
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface Props {
  showSuccessMessage: boolean
  setShowSuccessMessage: (show: boolean) => void
  errorMessages: string[] | ProductModels.IBulkError[]
  setErrorMessages: (errors: string[] | ProductModels.IBulkError[]) => void
  errorTitle: string
  setErrorTitle: (title: string) => void
  afterImport: () => void
}

const _ImportProductsDropzoneComponent = ({
  currentCompanyStore,
  currentUserStore,
  showSuccessMessage,
  setShowSuccessMessage,
  errorMessages,
  setErrorMessages,
  errorTitle,
  setErrorTitle,
  afterImport,
}: Props & InjectedProps) => {
  if (!currentCompanyStore || !currentCompanyStore.company) return null
  const [isUploading, setIsUploading] = useState(false)
  const [uploadStepsTaken, setUploadStepsTaken] = useState<number>(0)
  const [progressMessage, setProgressMessage] = useState<string>('')
  const [isImportZoneDisabled, setIsImportZoneDisabled] = useState<boolean>(false)

  const company = currentCompanyStore.company
  const totalProgressSteps = 5
  const uploadingMessage = 'Uploading file...'

  // This cannot be a State variable as it wouldn't be updated as fast as we need it to be (https://blog.logrocket.com/why-react-doesnt-update-state-immediately/)
  let hasErrorBeenDisplayed = false

  const setHasErrorBeenDisplayed = (val: boolean) => {
    hasErrorBeenDisplayed = val
  }

  const getProgressRate = () => {
    return Math.floor((uploadStepsTaken / totalProgressSteps) * 100)
  }

  const finishUploading = () => {
    setIsUploading(false)
    setUploadStepsTaken(0)
  }

  const onDrop = async (file: UploadFile<unknown>) => {
    try {
      setShowSuccessMessage(false)
      setErrorMessages([])
      setErrorTitle('')
      setHasErrorBeenDisplayed(false)

      setIsUploading(true)
      setProgressMessage(uploadingMessage)
      setUploadStepsTaken((prevSteps) => prevSteps + 1)

      const validatedFileObject: IFileObject = await validateDroppedFile(file)

      await uploadFile(validatedFileObject)
    } catch (error) {
      if (!hasErrorBeenDisplayed) {
        message.error(parseErrorMsg(error) || 'An error occurred while handling the file', 10)
      }
      finishUploading()
    }
  }

  const getCloudinaryUrls = async (
    modifiedRows: string[],
    validatedImageUrlsMap: Map<number, string>,
  ) => {
    try {
      const cloudinaryUrls = await uploadImagesAndGetCloudinaryUrlsSimultaneously(
        validatedImageUrlsMap,
        currentCompanyStore,
      )
      for (const [i, cloudinaryUrl] of cloudinaryUrls) {
        modifiedRows[i][CSVProductHeaders.IMAGE_URL] = cloudinaryUrl
      }
    } catch (error) {
      finishUploading()
      setErrorTitle('Error uploading "Image URL"')
      setErrorMessages([error as string])
      throw new Error('Error uploading Image URLs')
    }

    return modifiedRows
  }

  const validateImageUrls = (imageURLsMap: Map<number, string>) => {
    const validatedImageUrlsMap = new Map<number, string>()
    const urlsValidationErrors: string[] = []

    for (const [index, Url] of imageURLsMap) {
      const isCloudinaryUrl = Url.includes(CLOUDINARY)

      if (isCloudinaryUrl) continue

      const isValidImageUrl = GrRegex.isValidUrl(Url)

      if (!isValidImageUrl) {
        urlsValidationErrors.push(`Image URL "${Url}" is invalid.`)
        continue
      }

      validatedImageUrlsMap.set(index, Url)
    }

    return { validatedImageUrlsMap, urlsValidationErrors }
  }

  const parseAndSanitizeCSV = async (fileContent: string) => {
    let headersRow: string[] | null = null
    const imageUrlsMap = new Map<number, string>()

    const { data: rows, errors } = Papa.parse(fileContent, {
      header: true,
      skipEmptyLines: true,
    })

    if (errors && errors.length > 0) {
      console.error(errors)
      throw new Error('Error parsing CSV file.')
    }

    if (rows.length === 0) {
      throw new Error('The file has no data.')
    }

    if (rows.length > MAX_BULK_UPDATE_SIZE) {
      throw new Error(
        `Files should be ${MAX_BULK_UPDATE_SIZE} rows or less. ${rows.length} rows provided.`,
      )
    }

    headersRow = Object.keys(rows[0])

    if (!headersRow.includes(CSVProductHeaders.IMAGE_URL)) {
      throw new Error('"Image URL" column is missing.')
    }

    setUploadStepsTaken((prevSteps) => prevSteps + 1)

    const modifiedRows = []

    for (let i = 0; i < rows.length; i++) {
      const modifiedRow = { ...rows[i] }

      for (const columnName in rows[i]) {
        if (
          columnName !== CSVProductHeaders.DESCRIPTION &&
          columnName !== CSVProductHeaders.SHORT_DESCRIPTION
        ) {
          modifiedRow[columnName] = modifiedRow[columnName].replace(/[\r\n]/g, ' ')

          if (
            columnName === CSVProductHeaders.ID ||
            columnName === CSVProductHeaders.NAME ||
            columnName === CSVProductHeaders.SKU ||
            columnName === CSVProductHeaders.IS_ACTIVE ||
            columnName === CSVProductHeaders.IMAGE_URL ||
            columnName === CSVProductHeaders.SHOW_MAX_MIN ||
            columnName === CSVProductHeaders.UNIT ||
            columnName === CSVProductHeaders.STRAIN_TYPE
          ) {
            modifiedRow[columnName] = modifiedRow[columnName].trim()
          }
        }
      }
      modifiedRows.push(modifiedRow)

      const imageUrl = modifiedRow[CSVProductHeaders.IMAGE_URL]
      if (!imageUrl) continue

      imageUrlsMap.set(i, imageUrl)
    }

    const { validatedImageUrlsMap, urlsValidationErrors } = validateImageUrls(imageUrlsMap)

    if (urlsValidationErrors.length !== 0) {
      setErrorMessages(urlsValidationErrors)
      setErrorTitle('Error validating "Image URL"')
      throw new Error('Error validating "Image URL"')
    }

    if (validatedImageUrlsMap.size === 0 || validatedImageUrlsMap.size > 100) {
      const modifiedContent = Papa.unparse(modifiedRows)
      return modifiedContent
    }

    const modifiedRowsWithCloudinaryUrls = await getCloudinaryUrls(
      modifiedRows,
      validatedImageUrlsMap,
    )

    const modifiedContent = Papa.unparse(modifiedRowsWithCloudinaryUrls)

    return modifiedContent
  }

  const readCSV = async (fileObj: File | Blob | undefined): Promise<string> => {
    if (!fileObj) {
      throw new Error('The file object is missing')
    }

    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        if (reader.result && typeof reader.result === 'string') {
          resolve(reader.result)
        } else {
          reject(new Error('Failed to read file content'))
        }
      }
      reader.readAsText(fileObj, 'utf-8')
    })
  }

  const uploadImageUrlsToCloudinary = async (fileObj: File | Blob | undefined, type: string) => {
    try {
      const fileContent = await readCSV(fileObj)
      const updatedFileContentWithCloudinaryUrls = await parseAndSanitizeCSV(fileContent)

      if (!updatedFileContentWithCloudinaryUrls) return

      const updatedFileObj = new Blob([updatedFileContentWithCloudinaryUrls], { type })
      return updatedFileObj
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while uploading Image URLs', 10)
      setHasErrorBeenDisplayed(true)
      finishUploading()
      throw new Error('An error occurred while uploading the file')
    }
  }

  const validateDroppedFile = async (file: UploadFile<unknown>): Promise<IFileObject> => {
    const { name, type, originFileObj } = file
    const fileExtension = name.split('.').pop()

    if (!fileExtension || fileExtension !== 'csv') {
      finishUploading()
      throw new Error('Please upload a file with a .csv extension')
    }

    if (!originFileObj) {
      finishUploading()
      throw new Error(`The originFileObj of ${name} is unavailable.`)
    }

    const updatedFileObj: Blob | undefined = await uploadImageUrlsToCloudinary(originFileObj, type)

    const validatedFileObject: IFileObject = {
      fileObj: updatedFileObj || originFileObj,
      fullFileName: name,
      fileExtension,
      contentType: type,
    }
    return validatedFileObject
  }

  const uploadFile = async (fileObject: IFileObject) => {
    if (!fileObject || !fileObject.fileObj) {
      finishUploading()
      throw new Error(`The file object of ${fileObject.fullFileName} is unavailable.`)
    }

    const { fileObj, fileExtension, contentType } = fileObject

    try {
      const { fileName, signedUrl } = await currentCompanyStore.getSignedTempFileUploadUrl(
        fileExtension,
        contentType,
      )

      setUploadStepsTaken((prevSteps) => prevSteps + 1)
      handleS3Upload({ file: fileObj, fileName, signedUrl, contentType })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while uploading the file', 10)
      finishUploading()
    }
  }

  const handleS3Upload = ({
    file,
    fileName,
    signedUrl,
    contentType,
  }: {
    file: File | Blob
    fileName: string
    signedUrl: string
    contentType: string
  }): void => {
    const xhr = uploadFileToS3Bucket(file, signedUrl, contentType)

    xhr.onload = async () => {
      if (xhr.status !== 200) {
        finishUploading()
        message.error('An error occurred while uploading the file')
      }

      await xhrOnload(fileName)
    }
  }

  const xhrOnload = async (fileName: string) => {
    try {
      setUploadStepsTaken((prevSteps) => prevSteps + 1)

      await sleep(500) // to show the progress bar properly

      await productApi.bulkUpdateProductDetails(company.id, { fileName })

      message.success('File accepted', 5)

      setUploadStepsTaken((prevSteps) => prevSteps + 1)
      setShowSuccessMessage(true)
      setIsImportZoneDisabled(true)
      setTimeout(() => {
        setIsImportZoneDisabled(false)
        setShowSuccessMessage(false)
      }, 5 * 60 * 1000)

      await sleep(1500) // show the completed bar for 1.5 second
      finishUploading()
      afterImport()
    } catch (error) {
      const errorMessage = parseProductBulkUpdateErrorMsg(error)
      const errorTitle = parseProductBulkUpdateErrorTitle(error)

      if (errorMessage === undefined && isArray(errorTitle)) {
        const [mainTitle, ...remainingErrors] = errorTitle
        setErrorTitle(mainTitle)
        setErrorMessages(remainingErrors)
      } else if (isArray(errorMessage)) {
        setErrorTitle(errorTitle || 'Error validating file')
        setErrorMessages(errorMessage)
      } else {
        message.error(errorMessage || 'An error occurred while processing your file', 10)
      }

      finishUploading()
    }
  }

  const showErrorMessages = () => {
    return (
      <Alert
        className='mb-3 mt-4'
        type='error'
        message={
          <>
            <div className='font-weight-bold mb-1'>{errorTitle}</div>
            <ul className='pl-0 mb-0'>
              {errorMessages.map((error, index) => (
                <li
                  key={index}
                  style={{
                    listStyle: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '0.6rem',
                  }}
                >
                  <div style={{ marginRight: '0.6rem' }}>
                    <CloseCircleFilled style={{ color: 'red' }} />
                  </div>
                  {typeof error === 'object' && showObjectErrors(error)}
                  {typeof error === 'string' && error}
                </li>
              ))}
            </ul>
          </>
        }
      />
    )
  }

  const showObjectErrors = (error: ProductModels.IBulkError) => {
    return (
      <>
        {error.id && `Error processing row with product ID ${error.id}.`} {error.message}
      </>
    )
  }

  const canEditProducts = currentUserStore
    ? currentUserStore.hasPermissionAtEntity(PermissionNames.CAN_EDIT_PRODUCTS, company.id)
    : false

  const baseDraggerStyle = {
    margin: '18px 0px',
  }

  if (isUploading) {
    return (
      <div style={{ marginTop: '50px' }}>
        <Progress percent={getProgressRate()} />
        <div className='d-flex align-items-center justify-content-center mt-2'>
          <span className='mr-2'>{progressMessage}</span>
          <Spin className='mt-1' size='small' />
        </div>
      </div>
    )
  }

  return (
    <div className='mt-4'>
      {!canEditProducts && (
        <Alert
          className='mb-3'
          type='warning'
          showIcon
          style={{ width: 340 }}
          message={<span>You do not have permission to edit products.</span>}
        />
      )}

      <Upload.Dragger
        name='file'
        multiple={false}
        onChange={(info: UploadChangeParam<UploadFile<unknown>>) => onDrop(info.file)}
        style={
          canEditProducts
            ? baseDraggerStyle
            : { ...baseDraggerStyle, backgroundColor: 'rgba(0, 0, 0, 0.1)', opacity: 0.7 }
        }
        showUploadList={false}
        className='mt-2'
        disabled={!canEditProducts || isImportZoneDisabled}
      >
        <p className='ant-upload-drag-icon'>
          <Icon type='inbox' />
        </p>
        <div className='ant-upload-text'>Click or Drag to Upload</div>
      </Upload.Dragger>

      {errorMessages.length > 0 && showErrorMessages()}

      {showSuccessMessage && (
        <Alert
          message={
            <>
              Your file has been accepted and is being processed in the background.
              <br />
              It could take up to 10 minutes for the process to complete.
              <br />
              Please do not upload another file during that time.
            </>
          }
          showIcon
          type='success'
        />
      )}
    </div>
  )
}

export const ImportProductsDropZone = inject(
  'currentUserStore',
  'currentCompanyStore',
)(observer(_ImportProductsDropzoneComponent))
